import React, { useState, useRef, useEffect } from "react";
import { FieldError } from "react-hook-form";
import Label from "../form/Label";

interface FileUploadProps {
  name: string;
  register: any;
  error?: FieldError;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  onChange?: (filePath: string) => void;
}

const MyFileUpload: React.FC<FileUploadProps> = ({
  name,
  register,
  error,
  label,
  required = false,
  disabled = false,
  accept = ".pdf,.docx,.jpg,.jpeg,.png",
  multiple = false,
  maxSize = 5, // 5MB default
  onChange,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Register the field with react-hook-form
  const {
    ref: registerRef,
    onChange: registerOnChange,
    ...rest
  } = register(name);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isValidType = accept
      .split(",")
      .some((type) => file.type.includes(type.replace(".", "")));
    const isValidSize = file.size <= maxSize * 1024 * 1024;

    if (!isValidType || !isValidSize) {
      return;
    }

    setSelectedFile(file);

    // Generate preview URL for image
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl("");
    }

    // Convert file to base64 string
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Call the onChange prop with the base64 string
      if (onChange) {
        onChange(base64String);
      }
      // Call react-hook-form's onChange with the base64 string
      registerOnChange({
        target: {
          name,
          value: base64String,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Trigger onChange with empty string
    if (onChange) {
      onChange("");
    }
    // Call react-hook-form's onChange with empty string
    registerOnChange({
      target: {
        name,
        value: "",
      },
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="w-full">
      {label && (
        <Label>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </Label>
      )}
      <div className="mt-1">
        <input
          type="file"
          {...rest}
          ref={(e) => {
            registerRef(e);
            fileInputRef.current = e;
          }}
          onChange={handleFileChange}
          accept={accept}
          multiple={false} // Force single file upload
          disabled={disabled}
          className="hidden"
          id={`file-upload-${name}`}
        />
        <label
          htmlFor={`file-upload-${name}`}
          className={`cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <svg
            className="-ml-1 mr-2 h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          Upload File
        </label>
      </div>

      {selectedFile && (
        <div className="mt-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={selectedFile.name}
                  className="h-12 w-12 object-cover rounded"
                />
              ) : (
                <div className="h-12 w-12 flex items-center justify-center bg-gray-200 rounded">
                  <svg
                    className="h-6 w-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={removeFile}
                className="text-red-500 hover:text-red-700"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error.message}</p>}

      <p className="mt-2 text-sm text-gray-500">
        Supported formats: PDF, DOCX, JPG, PNG (Max size: {maxSize}MB)
      </p>
    </div>
  );
};

export default MyFileUpload;
